import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Linkedin } from "lucide-react";
import { Subpage, BeforeAfter, OrderCta } from "@/components/site/Subpage";
import { Seo, SITE_URL } from "@/components/site/Seo";
import { Label } from "@/components/site/Reveal";
import { POSTS } from "@/content/blog";
import { useAllPosts, usePost, shareImageUrl } from "@/lib/posts";
import { FIELDS } from "@/content/fields";

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export function BlogIndex() {
  const posts = useAllPosts();
  return (
    <Subpage>
      <Seo title="Resume Bullet Tips & Examples | Verbulat Blog" path="/blog"
        description="Short, practical tips for writing resume bullets: action verbs, real numbers, the action-task-result formula and tailoring keywords to job postings." />
      <div data-testid="blog-index">
        <Label>Resume bullet tips</Label>
        <h1 className="font-serif-d mt-5 text-5xl leading-[0.95] text-white sm:text-6xl">Write bullets that <span className="italic text-[var(--teal)]">prove it.</span></h1>
        <p className="mt-6 text-lg text-slate-300">Short, practical guides with before-and-after examples.</p>
        <ul className="mt-12 divide-y divide-white/10 border-y border-white/10">
          {posts.map((p) => (
            <li key={p.slug}>
              <Link to={`/blog/${p.slug}`} className="group block py-7" data-testid={`blog-link-${p.slug}`}>
                <h2 className="font-serif-d text-3xl text-white transition-colors group-hover:text-[var(--teal)]">{p.title}</h2>
                <p className="mt-2 text-slate-300">{p.description}</p>
                <p className="font-mono-d mt-3 flex items-center gap-2 text-xs text-slate-400">{p.readMins} min read <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></p>
              </Link>
            </li>
          ))}
        </ul>
        <FieldLinks />
      </div>
    </Subpage>
  );
}

export const FieldLinks = () => (
  <div className="mt-14">
    <p className="font-mono-d text-xs uppercase tracking-[0.2em] text-slate-400">Resume help by field</p>
    <div className="mt-4 flex flex-wrap gap-2">
      {FIELDS.map((f) => (
        <Link key={f.slug} to={`/resume-help/${f.slug}`} data-testid={`field-link-${f.slug}`}
          className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-[var(--teal)] hover:text-[var(--teal)]">
          {f.name}
        </Link>
      ))}
    </div>
  </div>
);

export function BlogPost() {
  const { slug } = useParams();
  const { post, loading, missing } = usePost(slug);
  const all = useAllPosts();
  if (missing) return <Navigate to="/blog" replace />;
  if (loading || !post) return <Subpage><div className="min-h-[50vh]" /></Subpage>;
  const idx = all.findIndex((p) => p.slug === post.slug);
  const next = all[(idx + 1) % all.length] || POSTS[0];
  const shareImg = shareImageUrl(post);
  const pageUrl = `${SITE_URL}/blog/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.description,
    datePublished: post.date, dateModified: post.date, mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
    image: `${SITE_URL}/og-image.png`, author: { "@type": "Organization", name: "Verbulat" },
    publisher: { "@type": "Organization", name: "Verbulat", logo: { "@type": "ImageObject", url: `${SITE_URL}/apple-touch-icon.png` } },
  };
  return (
    <Subpage>
      <Seo title={`${post.title} | Verbulat`} description={post.description} path={`/blog/${post.slug}`} type="article" jsonLd={jsonLd} />
      <article data-testid="blog-post">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[var(--teal)]" data-testid="blog-back-link"><ArrowLeft className="h-4 w-4" /> All tips</Link>
        <h1 className="font-serif-d mt-8 text-4xl leading-[1] text-white sm:text-6xl">{post.title}</h1>
        <p className="font-mono-d mt-5 text-xs text-slate-400">{fmtDate(post.date)} · {post.readMins} min read</p>
        <div className="mt-10 space-y-6 text-lg leading-relaxed text-slate-200">
          {post.sections.map((s, i) => (s.before
            ? <BeforeAfter key={i} before={s.before} after={s.after} />
            : (
              <section key={i}>
                {s.h && <h2 className="font-serif-d mb-2 text-3xl text-white">{s.h}</h2>}
                <p>{s.p}</p>
              </section>
            )))}
        </div>
        <p className="mt-6 text-xs text-slate-400">Examples are illustrative.</p>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="blog-share">
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" data-testid="blog-share-linkedin"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:border-[var(--teal)] hover:text-[var(--teal)]">
            <Linkedin className="h-4 w-4" /> Share on LinkedIn
          </a>
          {shareImg && (
            <a href={shareImg} target="_blank" rel="noreferrer" download data-testid="blog-share-image"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:border-[var(--teal)] hover:text-[var(--teal)]">
              <Download className="h-4 w-4" /> Before/after image
            </a>
          )}
        </div>
        <OrderCta />
        <Link to={`/blog/${next.slug}`} className="group mt-12 block border-t border-white/10 pt-8" data-testid="blog-next-link">
          <p className="font-mono-d text-xs uppercase tracking-[0.2em] text-slate-400">Next tip</p>
          <p className="font-serif-d mt-2 text-3xl text-white group-hover:text-[var(--teal)]">{next.title}</p>
        </Link>
      </article>
    </Subpage>
  );
}
